import {
  CardElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import moment from "moment"
import React, { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  getInvoiceData,
  publicPaymentInvoice,
} from "../../services/api/apiModule"
import { useEffect } from "react"
import { openToastify } from "../../store/actions/toastifyAction"
import { REDUX_TOASTIFY_DEFAULTS } from "../../store/config"

const InvoicePaymentPage = () => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const cardRef = useRef(null)
  const [searchParams] = useSearchParams()
  const invoiceId = searchParams.get("id")

  const [isLoading, setIsLoading] = useState(false)
  const [isCardComplete, setIsCardComplete] = useState(false)
  const [invoiceData, setInvoiceData] = useState({})
  const [invoiceAmount, setInvoiceAmount] = useState(0)
  const submittingRef = useRef(false)

  const handleGetInvoiceApi = async () => {
    try {
      setIsLoading(true)
      const response = await getInvoiceData(invoiceId)

      if (response.success) {
        setInvoiceData(response.invoice)
        setInvoiceAmount(response?.invoice?.paymentSummary?.dueAmount || 0)
      }
      console.log("handleGetInvoiceApi", response)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (invoiceId) {
      console.log("Invoice ID:", invoiceId)
      handleGetInvoiceApi()
    }
  }, [invoiceId])

  const formatCurrency = (value) =>
    value.toLocaleString("en-US", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    })

  const handleChange = () => {}

  const paymentAttemptId = crypto.randomUUID()
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (submittingRef.current) {
      return
    }

    submittingRef.current = true
    setIsLoading(true)
    try {
      const cardElement = elements.getElement(CardElement)
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        })

      if (paymentMethodError) {
        dispatch(
          openToastify(
            paymentMethodError.message,
            REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_ERROR(),
          ),
        )
        setIsLoading(false)
        return
      }

      const payload = {
        totalAmount: invoiceAmount,
        currency: "cad",
        invoiceId: invoiceId,
        paymentMethodId: paymentMethod.id,
        paymentAttemptId: paymentAttemptId,
      }

      const response = await publicPaymentInvoice(payload)

      if (response.success) {
        dispatch(
          openToastify(
            response.message || "Invoice Paid Sucessfully",
            REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_WARN(),
          ),
        )
        handleGetInvoiceApi()
      }
      console.log(response)
    } catch (error) {
      console.log(error, error.response.data.message)
      dispatch(
        openToastify(
          error.response.data.message || "Something Went Wrong",
          REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_WARN(),
        ),
      )
    } finally {
      submittingRef.current = false
      setIsLoading(false)
    }
  }

  const isSubmitDisabled = isLoading || !isCardComplete || invoiceAmount === 0

  return (
    <div className="min-h -screen flex-1 text-slate-900 flex items-start lg:items-center justify-center px-4 py-8 lg:py-16">
      {/* Abstract background shapes */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -right-10 h-52 w-52 rounded-full bg-gradient-to-br from-[#e9c46b] via-[#e3b74e] to-[#cc9d51] blur-3xl opacity-60" />
        <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-gradient-to-tr from-[#cc9d51] via-[#e9c46b] to-[#ac7134] blur-3xl opacity-50" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-yellow-200/70 to-transparent" />
      </div>

      <div className="relative w-full max-w-md lg:max-w-5xl">
        {/* Floating accent shape */}
        <div className="absolute -top-6 right-6 h-10 w-10 rounded-2xl bg-gradient-to-br from-[#e9c46b] to-[#ac7134] opacity-90 shadow-lg shadow-yellow-200/80 rotate-6" />
        <div className="absolute -bottom-4 left-10 h-8 w-8 rounded-3xl border border-yellow-200 bg-white/80 shadow-[0_15px_35px_rgba(15,23,42,0.12)] rotate-12" />

        <div className="relative overflow-hidden rounded-3xl border border-black bg-white/90 shadow-2xl shadow-yellow-light_medium/70 backdrop-blur-xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
                Invoice Payment
              </p>
              {/* <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                Send a little magic ✨
              </h1> */}
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                Fill out the details below and securely pay with Stripe.
              </p>
            </div>
            <div className="flex flex-col items-start sm:items-end">
              <span className="rounded-full border border-[#e3b74e] bg-[#e9c46b]/20 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#ac7134]">
                Secure Checkout
              </span>
              <span className="mt-1 text-[10px] text-slate-500">
                256-bit SSL • Powered by Stripe
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 2-column layout on desktop */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-10">
              {/* LEFT: form fields */}
              <div className="space-y-4">
                {/* Invoice amount */}
                <div className="space-y-2">
                  {/* Custom amount */}
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5">
                    <span className="text-sm text-slate-500">$</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={invoiceAmount}
                      onChange={(val) => {
                        setInvoiceAmount(val.target.value)
                      }}
                      placeholder="Enter amount"
                      className="w-full bg-transparent text-2xl text-slate-900 placeholder:text-slate-400 outline-none"
                      disabled
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-yellow-dark bg-[#fffaf0] px-4 py-3 text-xs sm:text-sm shadow-yellow-medium_dark">
                  <CardElement
                    onChange={(event) => setIsCardComplete(event.complete)}
                  />

                  {/* <PaymentElement
                    options={{
                      layout: "tabs",
                      // defaultValues: {
                      //   billingDetails: {
                      //     address: {
                      //       country: "CA",
                      //     },
                      //   },
                      // },
                    }}
                  /> */}
                </div>

                {/* Stripe CTA */}
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className={`group relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-4 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${
                      isSubmitDisabled
                        ? "cursor-not-allowed bg-slate-200 text-slate-400"
                        : "bg-gradient-to-r from-[#e3b74e] via-[#e9c46b] to-[#ac7134] text-slate-900 shadow-lg shadow-yellow-200/80 hover:brightness-105 focus:ring-[#e9c46b]"
                    }`}
                  >
                    {isLoading ? (
                      <span>Processing...</span>
                    ) : (
                      <span className="relative flex items-center gap-2">
                        <span>Pay securely with Stripe</span>
                        {!isSubmitDisabled && (
                          <span className="text-xs opacity-80 group-hover:translate-x-0.5 transition-transform">
                            ➝
                          </span>
                        )}
                      </span>
                    )}
                  </button>

                  {/* <p className="text-[11px] text-center text-slate-500">
                    By continuing, you agree to our{" "}
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-slate-700"
                    >
                      Terms
                    </button>{" "}
                    &{" "}
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-slate-700"
                    >
                      Privacy Policy
                    </button>
                    .
                  </p> */}
                </div>
              </div>

              {/* RIGHT: summary + CTA */}
              <div className="mt-6 lg:mt-0 lg:flex lg:flex-col lg:justify-between space-y-4 lg:space-y-6">
                {/* Price summary */}
                <div className="rounded-2xl border border-yellow-medium_dark bg-[#fffaf0] px-4 py-3 text-xs sm:text-sm shadow-[0_20px_45px_rgba(15,23,42,0.08)]">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Invoice</span>
                    <span className="font-medium text-slate-900">
                      {invoiceData?.invoiceId || ""}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-slate-700">Due Date</span>
                    <span className="font-medium text-slate-900">
                      {invoiceData?.paymentSummary?.dueDate}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Invoice Amount</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(invoiceData?.pricing?.total || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-6">
                    <span className="text-slate-700">Invoice Status</span>
                    <span
                      className={`font-medium uppercase text-white px-5 py-1 rounded-2xl ${invoiceAmount === 0 ? "bg-green-500" : "bg-red-500"}`}
                    >
                      {invoiceData?.paymentSummary?.paymentStatus}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-yellow-200 pt-2 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wide text-slate-700">
                        Due Total
                      </span>
                      <span className="text-[11px] text-slate-700">
                        Charged via Stripe
                      </span>
                    </div>
                    {invoiceAmount === 0 ? (
                      <>
                        <span className="text-lg lg:text-xl font-semibold text-slate-900 line-through">
                          {formatCurrency(invoiceData?.pricing?.total || 0)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-lg lg:text-xl font-semibold text-slate-900">
                          {formatCurrency(invoiceData?.pricing?.total || 0)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  {/* <button
                    onClick={() => {
                      cardRef.current?.scrollIntoView({ behavior: "smooth" })
                    }}
                    type="button"
                    className={`group relative flex items-center justify-center rounded-2xl px-2 py-1 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#fffdf7] bg-gradient-to-r from-[#e3b74e] via-[#e9c46b] to-[#ac7134] text-slate-900 shadow-lg shadow-yellow-200/80 hover:brightness-105 focus:ring-[#e9c46b]      
                      `}
                  >
                    View Invoice
                  </button>

                  <button>down</button>
                  <button>print</button> */}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default InvoicePaymentPage
